import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { toast } from '@/hooks/use-toast';

const TipCalculator = () => {
  const [billAmount, setBillAmount] = useState<number>(50);
  const [tipPercentage, setTipPercentage] = useState<number>(15);
  const [numberOfPeople, setNumberOfPeople] = useState<number>(1);
  
  const [tipAmount, setTipAmount] = useState<number>(7.5);
  const [totalAmount, setTotalAmount] = useState<number>(57.5);
  const [amountPerPerson, setAmountPerPerson] = useState<number>(57.5);
  
  // Add ref to the calculator container
  const calculatorRef = useRef<HTMLDivElement>(null);

  // Add effect to prevent scrolling on focus
  useEffect(() => {
    // Function to prevent scroll on focus
    const preventScrollOnFocus = (e: Event) => {
      e.preventDefault();
      // Keep focus but prevent scrolling
      if (calculatorRef.current) {
        calculatorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };

    // Get all focusable elements within the calculator
    const focusableElements = calculatorRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    // Add event listeners to prevent scroll
    focusableElements?.forEach((el) => {
      el.addEventListener('focus', preventScrollOnFocus);
    });

    // Clean up
    return () => {
      focusableElements?.forEach((el) => {
        el.removeEventListener('focus', preventScrollOnFocus);
      });
    };
  }, [calculatorRef]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Calculate tip, total, and per-person amounts whenever inputs change
  useEffect(() => {
    if (billAmount >= 0 && tipPercentage >= 0 && numberOfPeople >= 1) {
      const calculatedTip = billAmount * (tipPercentage / 100);
      const calculatedTotal = billAmount + calculatedTip;
      const calculatedPerPerson = calculatedTotal / numberOfPeople;
      
      setTipAmount(calculatedTip);
      setTotalAmount(calculatedTotal);
      setAmountPerPerson(calculatedPerPerson);
    }
  }, [billAmount, tipPercentage, numberOfPeople]);

  const handleBillAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value >= 0) {
      setBillAmount(value);
    } else {
      setBillAmount(0);
      toast({
        title: "Invalid amount",
        description: "Please enter a valid bill amount",
        variant: "destructive"
      });
    }
  };

  const handleNumberOfPeopleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 1) {
      setNumberOfPeople(value);
    } else {
      setNumberOfPeople(1);
      toast({
        title: "Invalid number of people",
        description: "Number of people must be at least 1",
        variant: "destructive"
      });
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <div ref={calculatorRef}>
      <Card className="border-0 shadow-none">
        <CardContent className="p-0">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="bill-amount" className="text-base font-medium">Bill Amount</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <Input
                      id="bill-amount"
                      type="number"
                      min="0"
                      step="0.01"
                      value={billAmount}
                      onChange={handleBillAmountChange}
                      className="pl-8"
                    />
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between items-center">
                    <Label htmlFor="tip-percentage" className="text-base font-medium">Tip Percentage</Label>
                    <span className="text-lg font-semibold">{tipPercentage}%</span>
                  </div>
                  <div className="mt-2">
                    <Slider
                      id="tip-percentage"
                      min={0}
                      max={30}
                      step={1}
                      value={[tipPercentage]}
                      onValueChange={(value) => setTipPercentage(value[0])}
                      className="py-4"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>0%</span>
                      <span>15%</span>
                      <span>30%</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="number-of-people" className="text-base font-medium">Number of People</Label>
                  <Input
                    id="number-of-people"
                    type="number"
                    min="1"
                    step="1"
                    value={numberOfPeople}
                    onChange={handleNumberOfPeopleChange}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
            
            {/* Results Section */}
            <div className="bg-blue-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">Summary</h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Bill Amount:</span>
                  <span className="font-medium">{formatCurrency(billAmount)}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Tip Amount ({tipPercentage}%):</span>
                  <span className="font-medium">{formatCurrency(tipAmount)}</span>
                </div>
                
                <div className="pt-3 border-t border-blue-200">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total Amount:</span>
                    <span className="font-semibold">{formatCurrency(totalAmount)}</span>
                  </div>
                </div>
                
                {numberOfPeople > 1 && (
                  <div className="pt-3 border-t border-blue-200">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Amount Per Person:</span>
                      <span className="font-semibold text-lg">{formatCurrency(amountPerPerson)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TipCalculator;
